import React from 'react';
import LoadingSpinner from '../components/LoadingSpinner';

const AnalyticsTab = ({ 
  videoAccess, 
  loading, 
  videoAccessPagination,
  onFetchVideoAccess,
  formatDate,
  analyticsSearchTerm,
  setAnalyticsSearchTerm
}) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acesso aos Vídeos</h2>
          <p className="text-gray-600">Relatório de acesso aos vídeos pelos usuários</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pesquisar por usuário
            </label>
            <input
              type="text"
              value={analyticsSearchTerm}
              onChange={(e) => setAnalyticsSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  onFetchVideoAccess(1, analyticsSearchTerm);
                }
              }}
              placeholder="Digite o nome ou email do usuário..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => onFetchVideoAccess(1, analyticsSearchTerm)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Pesquisar
            </button>
            <button
              onClick={() => {
                setAnalyticsSearchTerm("");
                onFetchVideoAccess(1, "");
              }}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner message="Carregando relatório..." />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Curso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vídeo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Acesso
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {videoAccess.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-12 h-12 text-gray-400 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-lg font-medium text-gray-900 mb-2">
                          Nenhum acesso registrado
                        </p>
                        <p className="text-gray-500">
                          Dados de acesso aos vídeos aparecerão aqui quando usuários assistirem
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  videoAccess.map((access, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">
                              {access.user_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {access.user_name}
                            </div>
                            <div className="text-sm text-gray-500">{access.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {access.course_title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {access.video_name || access.video_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(access.accessed_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {videoAccessPagination.total > 0 && (
            <div className="bg-white px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-700">
                Mostrando{" "}
                <span className="font-medium">
                  {(videoAccessPagination.current_page - 1) * videoAccessPagination.per_page + 1}
                </span>{" "}
                até{" "}
                <span className="font-medium">
                  {Math.min(
                    videoAccessPagination.current_page * videoAccessPagination.per_page,
                    videoAccessPagination.total
                  )}
                </span>{" "}
                de <span className="font-medium">{videoAccessPagination.total}</span> resultados
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onFetchVideoAccess(videoAccessPagination.current_page - 1, analyticsSearchTerm)}
                  disabled={!videoAccessPagination.has_prev}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from(
                    { length: Math.min(5, videoAccessPagination.total_pages) },
                    (_, i) => {
                      const startPage = Math.max(1, videoAccessPagination.current_page - 2);
                      const pageNum = startPage + i;
                      if (pageNum > videoAccessPagination.total_pages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => onFetchVideoAccess(pageNum, analyticsSearchTerm)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            pageNum === videoAccessPagination.current_page
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>

                <button
                  onClick={() => onFetchVideoAccess(videoAccessPagination.current_page + 1, analyticsSearchTerm)}
                  disabled={!videoAccessPagination.has_next}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
